import { supabase } from '../lib/supabase';
import { ucCodigoKeys } from '../lib/ucCodigo';
import { sugerirSaldoDistrato, type FaturaParaSaldoDistrato, type SugestaoSaldoDistrato } from '../lib/saldoDistrato';
import {
    CAMPOS_FATURA_DEBITO,
    faturasEmAberto,
    grafiasDocumento,
    montarDadosDocumentoUc,
    type DadosDocumentoUc,
    type FaturaDebito,
    type FaturaEmAberto,
    type FaturaEnderecoDoc,
    type NegociacaoParaDocumento,
    type UcParaDocumento,
} from '../lib/dadosDocumentoUc';

// Regra pura em src/lib/dadosDocumentoUc.ts. Aqui só as buscas.

type UcBusca = UcParaDocumento & {
    id?: number | string | null;
    numero_uc?: string | null;
    outro_numero_uc?: string | null;
    negociacao_id?: number | null;
};

const CAMPOS_NEG = 'cliente_nome, cpf_cnpj, tipo_cliente, email, whatsapp, email_financeiro, whatsapp_financeiro, nome_representante_legal, cpf_representante';

/** A negociação ligada à UC ou, sem vínculo, a mais recente com o mesmo número. */
async function buscarNegociacao(uc: UcBusca): Promise<NegociacaoParaDocumento | null> {
    if (uc.negociacao_id) {
        const { data } = await supabase.from('negociacoes').select(CAMPOS_NEG).eq('id', uc.negociacao_id).maybeSingle();
        if (data) return data as NegociacaoParaDocumento;
    }
    if (!uc.numero_uc) return null;
    const { data } = await supabase
        .from('negociacoes')
        .select(CAMPOS_NEG)
        .eq('numero_uc', uc.numero_uc)
        .order('created_at', { ascending: false })
        .limit(1);
    return (data?.[0] as NegociacaoParaDocumento) ?? null;
}

/** Outras negociações do mesmo CPF/CNPJ, da mais recente para a mais antiga. */
async function buscarNegociacoesDoDocumento(doc: string): Promise<NegociacaoParaDocumento[]> {
    const grafias = grafiasDocumento(doc);
    if (grafias.length === 0) return [];
    const { data } = await supabase
        .from('negociacoes')
        .select(CAMPOS_NEG)
        .in('cpf_cnpj', grafias)
        .order('created_at', { ascending: false })
        .limit(10);
    return (data as NegociacaoParaDocumento[]) ?? [];
}

/** Outras UCs do mesmo cliente — pelo documento do CLIENTE, não do titular da fatura. */
async function buscarOutrasUcsDoCliente(uc: UcBusca, doc: string): Promise<UcParaDocumento[]> {
    const grafias = grafiasDocumento(doc);
    if (grafias.length === 0) return [];
    let q = supabase
        .from('unidades_consumidoras')
        .select('email_cliente, whatsapp_cliente')
        .in('cpf_cnpj_cliente', grafias)
        .limit(20);
    if (uc.id != null) q = q.neq('id', uc.id);
    const { data } = await q;
    return (data as UcParaDocumento[]) ?? [];
}

/** O endereço da fatura mais recente da UC que trouxe endereço. */
async function buscarEnderecoFatura(uc: UcBusca): Promise<FaturaEnderecoDoc | null> {
    const chaves = ucCodigoKeys(uc.numero_uc, uc.outro_numero_uc);
    if (chaves.length === 0) return null;
    const { data } = await supabase
        .from('faturas')
        .select('end_logradouro, end_numero, end_bairro, end_cep, end_cidade, end_uf')
        .in('uc_codigo_norm', chaves)
        .not('end_logradouro', 'is', null)
        .neq('end_logradouro', '')
        .order('created_at', { ascending: false })
        .limit(1);
    return (data?.[0] as FaturaEnderecoDoc) ?? null;
}

/**
 * Dados do cliente da UC completados pela negociação e pela fatura.
 *
 * Nunca lança: se uma busca falhar (RLS, rede), devolve o que a UC sozinha dá —
 * o modal já estava preenchido com isso.
 */
export async function carregarDadosDocumentoUc(uc: UcBusca): Promise<DadosDocumentoUc> {
    const doc = uc.cpf_cnpj_cliente || uc.cpf_cnpj_titular || '';
    const [neg, negsDoDoc, fatura, outrasUcs] = await Promise.all([
        buscarNegociacao(uc).catch(() => null),
        buscarNegociacoesDoDocumento(doc).catch(() => []),
        buscarEnderecoFatura(uc).catch(() => null),
        buscarOutrasUcsDoCliente(uc, uc.cpf_cnpj_cliente || '').catch(() => []),
    ]);
    return montarDadosDocumentoUc(uc, [neg, ...negsDoDoc], fatura, outrasUcs);
}

/**
 * Faturas da UC que ainda são dívida, pelas chaves de `chavesFaturasUc`.
 * Lança em erro de banco: quem chama decide o que mostrar.
 */
export async function buscarFaturasEmAberto(chaves: string[]): Promise<FaturaEmAberto[]> {
    if (chaves.length === 0) return [];
    const { data, error } = await supabase
        .from('faturas')
        .select(CAMPOS_FATURA_DEBITO)
        .in('uc_codigo_norm', chaves);
    if (error) throw error;
    return faturasEmAberto((data ?? []) as unknown as FaturaDebito[]);
}

/**
 * Saldo de créditos das UCs pela conta mais recente (regra de src/lib/saldoDistrato.ts).
 * Mesma consulta que o distrato faz; aqui para o aditivo de transferência, que
 * também leva o saldo da UC antiga e abria com o campo vazio.
 */
export async function sugerirSaldoUcs(
    lista: { chaves: string[]; desconto: number | null }[],
): Promise<SugestaoSaldoDistrato | null> {
    const chaves = lista.map(u => u.chaves);
    if (chaves.length === 0) return null;
    const { data, error } = await supabase
        .from('faturas')
        .select('uc_codigo_norm, mes_referencia, created_at, status_fatura, saldo_ugs_vigor, tarifa_injecao_gd1, valor_tarifa_distribuidora, desconto_tarifa')
        .in('uc_codigo_norm', Array.from(new Set(chaves.flat())))
        .order('created_at', { ascending: false })
        .limit(300);
    if (error) throw error;
    return sugerirSaldoDistrato((data || []) as FaturaParaSaldoDistrato[], chaves, lista.map(u => u.desconto));
}
